import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import kebabCase from 'lodash/kebabCase';

import { idToLink } from 'inside-out/managers/navManager';
import { extractAudiences } from 'inside-out/managers/resourceLibraryManager';

import { Link } from 'inside-out/components/shared/link';
import { Cards } from 'inside-out/components/shared/cards';
import { ResourceCard } from 'inside-out/components/shared/cards/resourceCard';
import { PageHeader } from 'inside-out/components/shared/pageHeader';

import {
	innerWrapperStyle,
	wrapperStyle,
	aboutCardsStyle,
	aboutCardStyle,
	titleStyle,
	textStyle,
	statisticsWrapStyle,
	statisticsLeftStyle,
	statisticsCenterStyle,
	statisticsRightStyle,
	statisticStyle,
	greyStatisticsWrapStyle,
	greyStatisticsColumnStyle,
	greyTitleStyle,
	blockquoteStyle,
	disorderWrapStyle,
	disorderStyle,
	treatmentBannerStyle,
	treatmentBannerInnerStyle,
	experienceCardStyle,
	experienceCardInnerStyle,
	experienceTitleStyle,
	purpleImageStyle,
} from 'inside-out/page-styles/about-eating-disorders';

import StatisticsChart from 'inside-out/images/about-eating-disorders/statistics-chart.svg';

import {
	aqua,
	darkBlueGrey,
	contentSection,
	cardSection,
	tabletPortraitMaxWidthBreakpoint,
} from 'inside-out/global-styles';
import { descriptionToHex } from 'inside-out/managers/colourManager';

const ResourcePropType = PropTypes.shape({
	title: PropTypes.string,
	type: PropTypes.string,
	date: PropTypes.string,
	fields: PropTypes.shape({
		thumbnail_image: PropTypes.shape({
			childImageSharp: PropTypes.shape({
				resize: PropTypes.shape({
					src: PropTypes.string.isRequired,
				}).isRequired,
			}).isRequired,
		}).isRequired,
	}).isRequired,
	attachments: PropTypes.arrayOf(
		PropTypes.shape({
			vimeo_url: PropTypes.string,
		})
	),
}).isRequired;

export default class AboutEatingDisordersPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							teal_heading: PropTypes.string.isRequired,
							teal_body: PropTypes.string.isRequired,

							heading_1: PropTypes.string.isRequired,
							body_1: PropTypes.string.isRequired,
							quotes: PropTypes.arrayOf(
								PropTypes.shape({
									quote: PropTypes.string.isRequired,
									colour: PropTypes.string.isRequired,
								})
							),

							heading_2: PropTypes.string.isRequired,
							statistic: PropTypes.string.isRequired,
							body_2: PropTypes.string.isRequired,
							pie_chart_data: PropTypes.arrayOf(
								PropTypes.shape({
									value: PropTypes.string.isRequired,
									value_description: PropTypes.string.isRequired,
									cohort_description: PropTypes.string.isRequired,
									condition: PropTypes.string.isRequired,
									colour: PropTypes.string.isRequired,
								})
							),

							heading_3: PropTypes.string.isRequired,
							statistic_1_3: PropTypes.string.isRequired,
							statistic_description_1_3: PropTypes.string.isRequired,
							statistic_2_3: PropTypes.string.isRequired,
							statistic_description_2_3: PropTypes.string.isRequired,
							statistic_3_3: PropTypes.string.isRequired,
							statistic_description_3_3: PropTypes.string.isRequired,

							quote: PropTypes.string.isRequired,

							video_link_1: PropTypes.string.isRequired,
							video_thumbnail_1: PropTypes.string.isRequired,
							video_subject_1: PropTypes.string.isRequired,
							video_description_1: PropTypes.string.isRequired,
							video_link_2: PropTypes.string.isRequired,
							video_thumbnail_2: PropTypes.string.isRequired,
							video_subject_2: PropTypes.string.isRequired,
							video_description_2: PropTypes.string.isRequired,
							video_link_3: PropTypes.string.isRequired,
							video_thumbnail_3: PropTypes.string.isRequired,
							video_subject_3: PropTypes.string.isRequired,
							video_description_3: PropTypes.string.isRequired,

							heading_4: PropTypes.string.isRequired,
							body_4: PropTypes.string.isRequired,
							eating_disorders_list: PropTypes.arrayOf(
								PropTypes.shape({
									heading: PropTypes.string.isRequired,
									body: PropTypes.string.isRequired,
									colour: PropTypes.string.isRequired,
								})
							),

							purple_banner_text: PropTypes.string.isRequired,
							purple_banner_image: PropTypes.string.isRequired,

							resource_1: ResourcePropType,
							resource_2: ResourcePropType,
							resource_3: ResourcePropType,
							resource_4: ResourcePropType,
						}).isRequired,
					})
				).isRequired,
			}).isRequired,
		}).isRequired,
	};

	render() {
		const pageSettings = this.props.data.page.edges[0].node;

		const resources = [
			pageSettings.resource_1,
			pageSettings.resource_2,
			pageSettings.resource_3,
			pageSettings.resource_4,
		];

		return (
			<div className={wrapperStyle}>
				<Helmet title={pageSettings.teal_heading}>
					<meta property="og:type" content="website"/>
			    <meta property="og:title" content={pageSettings.teal_heading}/>
			    <meta name="description" content={ pageSettings.teal_body }/>
			    <meta property="og:description" content={ pageSettings.teal_body }/>
				</Helmet>

				<PageHeader
					background={aqua}
					titleText={pageSettings.teal_heading}
					subTitleText={pageSettings.teal_body}
					subTitleTextColor="#fff"
					titleTextColor="#fff"
				/>

				<section className={contentSection('#fff')}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{pageSettings.heading_1}</h1>
						</header>
						<div
							className={textStyle}
							dangerouslySetInnerHTML={{
								__html: pageSettings.body_1,
							}}
						/>
					</div>

					<div className={innerWrapperStyle}>
						<div className={aboutCardsStyle}>
							<Cards
								columns={2}
								scrollable={false}
								columnsAt={{
									[`max-width: ${tabletPortraitMaxWidthBreakpoint}`]: 1,
								}}
							>
								{pageSettings.quotes.map((quoteItem, i) => (
									<p
										key={i}
										data-component="Card"
										className={aboutCardStyle(descriptionToHex(quoteItem.colour))}
									>
										<span>{quoteItem.quote}</span>
									</p>
								))}
							</Cards>
						</div>
					</div>
				</section>

				<section className={contentSection()}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{pageSettings.heading_2}</h1>
						</header>

						<div className={statisticsWrapStyle}>
							<div className={statisticsLeftStyle}>
								<h2>{pageSettings.statistic}</h2>
								<p>{pageSettings.body_2}</p>
							</div>
							<div className={statisticsCenterStyle}>
								<img src={StatisticsChart} alt="" />
							</div>
							<div className={statisticsRightStyle}>
								<ul>
									{pageSettings.pie_chart_data.map((data, i) => (
										<li key={i} className={statisticStyle(kebabCase(data.condition))}>
											<h2>{data.value_description}</h2>
											<p>
												{data.cohort_description} <span>{data.condition}</span>
											</p>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				</section>

				<section className={contentSection(darkBlueGrey, 80)}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={greyTitleStyle}>{pageSettings.heading_3}</h1>
						</header>

						<div className={greyStatisticsWrapStyle}>
							<div className={greyStatisticsColumnStyle('anorexia-nervosa')}>
								<em>
									{pageSettings.statistic_1_3}
									<span>{pageSettings.statistic_unit_1_3}</span>
								</em>
								<div
									dangerouslySetInnerHTML={{
										__html: pageSettings.statistic_description_1_3,
									}}
								/>
							</div>
							<div className={greyStatisticsColumnStyle('anorexia-nervosa')}>
								<em>
									{pageSettings.statistic_2_3}
									<span>{pageSettings.statistic_unit_2_3}</span>
								</em>
								<div
									dangerouslySetInnerHTML={{
										__html: pageSettings.statistic_description_2_3,
									}}
								/>
							</div>
							<div className={greyStatisticsColumnStyle('bulimia-nervosa')}>
								<em>
									{pageSettings.statistic_3_3}
									<span>{pageSettings.statistic_unit_3_3}</span>
								</em>
								<div
									dangerouslySetInnerHTML={{
										__html: pageSettings.statistic_description_3_3,
									}}
								/>
							</div>
						</div>
					</div>
				</section>

				<section className={contentSection('#fff')}>
					<div className={innerWrapperStyle}>
						<blockquote className={blockquoteStyle}>{pageSettings.quote}</blockquote>
					</div>
				</section>

				<section className={contentSection()}>
					<div className={innerWrapperStyle}>
						<div className={cardSection(0)}>
							<header>
								<h1 className={experienceTitleStyle}>People&apos;s Experiences</h1>
							</header>
							<Cards columns={3}>
								<Link href={pageSettings.video_link_1} data-component="Card">
									<div className={experienceCardStyle(pageSettings.video_thumbnail_1, true)}>
										<div className={experienceCardInnerStyle}>
											<h2>{pageSettings.video_subject_1}</h2>
											<p>{pageSettings.video_description_1}</p>
										</div>
									</div>
								</Link>
								<Link href={pageSettings.video_link_2} data-component="Card">
									<div className={experienceCardStyle(pageSettings.video_thumbnail_2, true)}>
										<div className={experienceCardInnerStyle}>
											<h2>{pageSettings.video_subject_2}</h2>
											<p>{pageSettings.video_description_2}</p>
										</div>
									</div>
								</Link>
								<Link href={pageSettings.video_link_3} data-component="Card">
									<div className={experienceCardStyle(pageSettings.video_thumbnail_3, true)}>
										<div className={experienceCardInnerStyle}>
											<h2>{pageSettings.video_subject_3}</h2>
											<p>{pageSettings.video_description_3}</p>
										</div>
									</div>
								</Link>
							</Cards>
						</div>
					</div>
				</section>

				<section className={contentSection('#fff')}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>{pageSettings.heading_4}</h1>
						</header>
						<div
							className={textStyle}
							dangerouslySetInnerHTML={{
								__html: pageSettings.body_4,
							}}
						/>
						<ul className={disorderWrapStyle}>
							{pageSettings.eating_disorders_list.map((disorder, i) => (
								<li key={i} className={disorderStyle(descriptionToHex(disorder.colour))}>
									<h2>{disorder.heading}</h2>
									<div
										className={textStyle}
										dangerouslySetInnerHTML={{
											__html: disorder.body,
										}}
									/>
								</li>
							))}
						</ul>
					</div>
				</section>

				{pageSettings.purple_banner_text && (
					<div className={treatmentBannerStyle}>
						<div className={innerWrapperStyle}>
							<div className={treatmentBannerInnerStyle}>
								<div
									dangerouslySetInnerHTML={{
										__html: pageSettings.purple_banner_text,
									}}
								/>
							</div>
						</div>
						<div className={purpleImageStyle(pageSettings.purple_banner_image)} />
					</div>
				)}

				<section className={contentSection()}>
					<div className={innerWrapperStyle}>
						<header>
							<h1 className={titleStyle}>Resources</h1>
						</header>
						<Cards columns={4}>
							{resources.map((node, index) => (
								<ResourceCard
									key={index}
									id={node.id}
									to={idToLink(node.id)}
									background={node.fields && node.fields.thumbnail_image.childImageSharp.resize.src}
									audiences={extractAudiences(node.categories)}
									hasVideo={!!node.attachments.find(a => a.vimeo_url)}
									title={node.title}
									body={node.extract}
								/>
							))}
						</Cards>
					</div>
				</section>
			</div>
		);
	}
}

export const query = graphql`
	query AboutEatingDisordersPageQuery {
		page: allContentJson(
			filter: { id: { regex: "/content\\/about-eating-disorders-page\\.json/" } }
		) {
			edges {
				node {
					teal_heading
					teal_body

					heading_1
					body_1
					quotes {
						quote
						colour
					}

					heading_2
					statistic
					body_2
					pie_chart_data {
						value
						value_description
						cohort_description
						condition
						colour
					}

					heading_3
					statistic_1_3
					statistic_unit_1_3
					statistic_description_1_3
					statistic_2_3
					statistic_unit_2_3
					statistic_description_2_3
					statistic_3_3
					statistic_unit_3_3
					statistic_description_3_3

					quote

					video_link_1
					video_thumbnail_1
					video_subject_1
					video_description_1
					video_link_2
					video_thumbnail_2
					video_subject_2
					video_description_2
					video_link_3
					video_thumbnail_3
					video_subject_3
					video_description_3

					heading_4
					body_4
					eating_disorders_list {
						heading
						body
						colour
					}

					purple_banner_text
					purple_banner_image
					resource_1 {
						...resource
					}
					resource_2 {
						...resource
					}
					resource_3 {
						...resource
					}
					resource_4 {
						...resource
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/about-eating-disorders/index.js